import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Skeleton from '../components/shared/ui/Skeleton';
import Breadcrumbs from '../components/shared/Breadcrumbs';
import { useCurrency } from '../components/CurrencyContext';
import EnquiryModal from '../components/packages-listing/EnquiryModal';
import PackageCard from '../components/packages-listing/PackageCard';
import { getPackagesLandingPage } from '../api/packagesListing';
import { resolveAssetUrl } from '../utils/resolveAssetUrl';

const normalizeKey = (value) => (value || '').toString().trim().toLowerCase();
const sectionKeyOf = (section) => normalizeKey(section?.sectionKey || section?.key);

const sectionCardClass =
  'rounded-[30px] border border-[#dde3ef] bg-white p-6 shadow-[0_18px_50px_rgba(15,23,42,0.06)] md:p-8';

const SearchBar = ({ onSubmit, values, onChange }) => (
  <form
    onSubmit={onSubmit}
    className="grid gap-3 rounded-[30px] border border-white/10 bg-white/95 p-3 shadow-[0_20px_60px_rgba(8,27,58,0.22)] backdrop-blur md:grid-cols-[1.4fr_1fr_0.8fr_auto]"
  >
    <label className="rounded-[22px] border border-[#d9e0eb] px-4 py-3">
      <span className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400">Destination</span>
      <input
        className="mt-1 w-full border-none bg-transparent p-0 text-sm font-medium text-slate-800 outline-none"
        placeholder="Goa, Kerala, Andaman..."
        value={values.destination}
        onChange={(event) => onChange('destination', event.target.value)}
      />
    </label>
    <label className="rounded-[22px] border border-[#d9e0eb] px-4 py-3">
      <span className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400">Start date</span>
      <input
        type="date"
        className="mt-1 w-full border-none bg-transparent p-0 text-sm font-medium text-slate-800 outline-none"
        value={values.startDate}
        onChange={(event) => onChange('startDate', event.target.value)}
      />
    </label>
    <label className="rounded-[22px] border border-[#d9e0eb] px-4 py-3">
      <span className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400">Travellers</span>
      <input
        type="number"
        min="1"
        className="mt-1 w-full border-none bg-transparent p-0 text-sm font-medium text-slate-800 outline-none"
        value={values.travellers}
        onChange={(event) => onChange('travellers', event.target.value)}
      />
    </label>
    <button
      type="submit"
      className="rounded-[22px] bg-[#d69232] px-6 py-4 text-sm font-semibold text-white transition hover:bg-[#bf7d22]"
    >
      Search
    </button>
  </form>
);

const PackagesLanding = () => {
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [enquiryPkg, setEnquiryPkg] = useState(null);
  const [heroSearch, setHeroSearch] = useState({
    destination: '',
    startDate: '',
    travellers: '2',
  });

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    getPackagesLandingPage()
      .then((res) => {
        if (!active) return;
        setData(res?.data || res || null);
      })
      .catch((err) => {
        if (!active) return;
        setError(err.message || 'Unable to load package landing page.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!data?.config) return;
    const title = data.config.seo_title || 'Homevilas Travel Packages';
    const description =
      data.config.seo_description ||
      'Discover premium Homevilas tours and packages with dynamic destinations, themes, offers, and curated itineraries.';
    document.title = title;

    let tag = document.querySelector('meta[name="description"]');
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('name', 'description');
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', description);
  }, [data]);

  const sections = useMemo(
    () =>
      (data?.config?.sections || [])
        .filter((section) => section?.enabled !== false)
        .sort((a, b) => Number(a.order || 0) - Number(b.order || 0)),
    [data]
  );

  const listingSettings = data?.listing_settings || {};
  const findSection = (key) => sections.find((section) => sectionKeyOf(section) === key);

  const handleSearch = (event) => {
    event.preventDefault();
    const params = new URLSearchParams();
    if (heroSearch.destination.trim()) params.set('destination', heroSearch.destination.trim());
    if (heroSearch.startDate) params.set('startDate', heroSearch.startDate);
    if (heroSearch.travellers) params.set('travellers', heroSearch.travellers);
    navigate(`/packages/list${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const renderHero = (section) => {
    const content = section?.content || {};
    const backgroundImage = resolveAssetUrl(content.heroImageDesktop || content.heroImageMobile);
    return (
      <section
        className="relative overflow-hidden bg-[linear-gradient(120deg,#071938_0%,#0c2e52_50%,#0f766e_100%)] text-white"
        style={
          backgroundImage
            ? {
                backgroundImage: `linear-gradient(120deg, rgba(7,25,56,0.9), rgba(12,46,82,0.76), rgba(15,118,110,0.68)), url(${backgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : undefined
        }
      >
        <div className="mx-auto max-w-7xl px-4 py-12 md:py-20">
          <Breadcrumbs
            items={[{ label: 'Home', to: '/' }, { label: 'Packages' }]}
            className="text-white/65"
            linkClassName="text-white/90"
            separatorClassName="text-white/35"
          />
          <div className="mt-8 max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.34em] text-[#d7b36a]">
              {content.eyebrow || 'Homevilas Journeys'}
            </p>
            <h1 className="mt-4 font-homevilas text-4xl font-semibold leading-tight md:text-6xl">
              {content.heading || 'Journeys crafted for refined travel.'}
            </h1>
            <p className="mt-4 text-base leading-8 text-white/78 md:text-lg">
              {content.subheading ||
                'Handpicked private stays, curated routes, and high-touch planning for every departure.'}
            </p>
          </div>
          {content.showSearchBar !== false ? (
            <div className="mt-10 max-w-5xl">
              <SearchBar
                onSubmit={handleSearch}
                values={heroSearch}
                onChange={(key, value) => setHeroSearch((prev) => ({ ...prev, [key]: value }))}
              />
            </div>
          ) : null}
        </div>
      </section>
    );
  };

  const renderDestinations = (section) => {
    const content = section?.content || {};
    const maxItems = Math.max(1, Number(content.maxItems) || 8);
    const items = (data?.featured_destinations || []).slice(0, maxItems);
    if (!items.length) return null;

    return (
      <section className="mx-auto max-w-7xl px-4 py-14">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9ba6ba]">Destinations</p>
            <h2 className="mt-2 font-homevilas text-4xl font-semibold text-[#0d213f]">
              {content.heading || 'Featured destinations'}
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              {content.subheading || 'Signature regions curated by Homevilas travel designers.'}
            </p>
          </div>
          <Link
            to="/packages/list"
            className="rounded-full border border-[#d4dbe6] px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-slate-600"
          >
            View all
          </Link>
        </div>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((item) => (
            <Link
              key={item.id || item.slug || item.name}
              to={`/packages/list?destination=${encodeURIComponent(item.slug || item.name)}`}
              className="group overflow-hidden rounded-[28px] border border-[#dde3ef] bg-white shadow-[0_16px_40px_rgba(15,23,42,0.06)] transition hover:-translate-y-1 hover:shadow-[0_24px_50px_rgba(15,23,42,0.1)]"
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={resolveAssetUrl(item.image_url) || 'https://picsum.photos/seed/homevilas-destination/900/700'}
                  alt={item.name}
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/65 via-transparent to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                  <p className="text-xl font-semibold">{item.name}</p>
                  <p className="mt-1 text-sm text-white/75">{item.trip_count || 0} curated journeys</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    );
  };

  const renderPackagesGrid = (title, subtitle, items, maxItems) => {
    const list = (items || []).slice(0, maxItems);
    if (!list.length) return null;

    return (
      <section className="mx-auto max-w-7xl px-4 py-14">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9ba6ba]">Packages</p>
            <h2 className="mt-2 font-homevilas text-4xl font-semibold text-[#0d213f]">{title}</h2>
            <p className="mt-2 text-sm text-slate-500">{subtitle}</p>
          </div>
          <Link
            to="/packages/list"
            className="rounded-full border border-[#d4dbe6] px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-slate-600"
          >
            View more
          </Link>
        </div>
        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {list.map((pkg) => (
            <PackageCard
              key={pkg.id}
              pkg={pkg}
              formatPrice={formatCurrency}
              labels={listingSettings.labels || {}}
              onEnquire={() => setEnquiryPkg(pkg)}
              showQuickBadges={listingSettings.show_quick_badges ?? true}
              showEnquiryCta={listingSettings.show_enquiry_cta ?? true}
              showReviewsCount={listingSettings.show_reviews_count ?? true}
            />
          ))}
        </div>
      </section>
    );
  };

  const renderThemes = (section) => {
    const content = section?.content || {};
    const items = data?.themes || [];
    if (!items.length) return null;

    return (
      <section className="mx-auto max-w-7xl px-4 py-14">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9ba6ba]">Themes</p>
          <h2 className="mt-2 font-homevilas text-4xl font-semibold text-[#0d213f]">
            {content.heading || 'Explore by theme'}
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            {content.subheading || 'Choose journeys by mood, occasion, and travel style.'}
          </p>
        </div>
        <div className="mt-6 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {items.map((theme) => (
            <Link
              key={theme.id || theme.slug}
              to={`/packages/list?themes=${encodeURIComponent(theme.slug || theme.id)}`}
              className="flex items-center justify-between rounded-[24px] border border-[#dde3ef] bg-white px-5 py-4 shadow-[0_12px_30px_rgba(15,23,42,0.05)] transition hover:-translate-y-0.5 hover:border-[#c8d3e5]"
            >
              <div className="flex items-center gap-3">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#0d1f3b] text-white">
                  <i className={theme.icon || 'bi bi-compass'} />
                </span>
                <div>
                  <p className="text-sm font-semibold text-[#15243d]">{theme.name}</p>
                  <p className="text-xs text-slate-500">{theme.description || 'Curated journeys and experiences.'}</p>
                </div>
              </div>
              <i className="bi bi-arrow-right text-slate-400" />
            </Link>
          ))}
        </div>
      </section>
    );
  };

  const renderListingPreview = (section) => {
    const content = section?.content || {};
    const items = data?.listing_preview || [];
    if (!items.length) return null;

    return (
      <section className="mx-auto max-w-7xl px-4 py-14">
        <div className="grid gap-6 lg:grid-cols-[290px_minmax(0,1fr)]">
          <div className="rounded-[30px] bg-[#0d1f3b] p-6 text-white shadow-[0_20px_50px_rgba(8,27,58,0.16)]">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-white/55">Tailor-made packages</p>
            <h2 className="mt-3 font-homevilas text-3xl font-semibold">
              {content.heading || 'Filterable package preview'}
            </h2>
            <p className="mt-3 text-sm leading-7 text-white/72">
              {content.subheading || 'Preview the same compact cards and dynamic filters available on the full listing page.'}
            </p>
            <div className="mt-6 space-y-3 rounded-[24px] border border-white/10 bg-white/5 p-4 text-sm text-white/75">
              <p>Destination filters</p>
              <p>Theme filters</p>
              <p>Budget and duration ranges</p>
              <p>Sticky desktop sidebar and mobile drawer</p>
            </div>
            <Link
              to="/packages/list"
              className="mt-6 inline-flex rounded-full bg-white px-5 py-3 text-xs font-semibold uppercase tracking-[0.24em] text-[#081b3a]"
            >
              View more
            </Link>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {items.map((pkg) => (
              <PackageCard
                key={pkg.id}
                pkg={pkg}
                formatPrice={formatCurrency}
                labels={listingSettings.labels || {}}
                onEnquire={() => setEnquiryPkg(pkg)}
                showQuickBadges={listingSettings.show_quick_badges ?? true}
                showEnquiryCta={listingSettings.show_enquiry_cta ?? true}
                showReviewsCount={listingSettings.show_reviews_count ?? true}
              />
            ))}
          </div>
        </div>
      </section>
    );
  };

  const renderOffers = (section) => {
    const content = section?.content || {};
    const items = data?.offers || [];
    if (!items.length) return null;
    return (
      <section className="bg-white/50 py-14">
        <div className="mx-auto max-w-7xl px-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9ba6ba]">Offers</p>
            <h2 className="mt-2 font-homevilas text-4xl font-semibold text-[#0d213f]">
              {content.heading || 'Limited-time offers'}
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              {content.subheading || 'Privileged rates and value-add experiences for selected departures.'}
            </p>
          </div>
          <div className="mt-6 grid gap-4 lg:grid-cols-3">
            {items.map((offer) => (
              <a
                key={offer.id}
                href={offer.target_url || offer.cta_link || '/packages/list'}
                className="group overflow-hidden rounded-[28px] border border-[#dde3ef] bg-white shadow-[0_16px_40px_rgba(15,23,42,0.06)]"
              >
                <div className="grid gap-0 sm:grid-cols-[140px_minmax(0,1fr)] lg:grid-cols-1">
                  <div className="h-48 overflow-hidden sm:h-full lg:h-44">
                    <img
                      src={resolveAssetUrl(offer.image_url) || 'https://picsum.photos/seed/homevilas-offer/900/700'}
                      alt={offer.title}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9ba6ba]">Offer</p>
                    <h3 className="mt-2 text-xl font-semibold text-[#15243d]">{offer.title}</h3>
                    <p className="mt-2 text-sm leading-7 text-slate-500">{offer.subtitle}</p>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>
    );
  };

  const renderReviews = (section) => {
    const content = section?.content || {};
    const items = data?.traveler_stories || [];
    if (!items.length) return null;
    return (
      <section className="mx-auto max-w-7xl px-4 py-14">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9ba6ba]">Reviews</p>
          <h2 className="mt-2 font-homevilas text-4xl font-semibold text-[#0d213f]">
            {content.heading || 'Traveller stories'}
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            {content.subheading || 'Recent journeys and curated feedback from guests.'}
          </p>
        </div>
        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((item) => (
            <article key={item.id} className={sectionCardClass}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-base font-semibold text-[#13223b]">
                    {item.customer_name || item.name || 'Homevilas guest'}
                  </p>
                  <p className="text-xs text-slate-500">
                    {item.customer_location || item.role || item.package_title || 'Curated journey'}
                  </p>
                </div>
                <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                  {Number(item.rating || 4.8).toFixed(1)}
                </span>
              </div>
              <p className="mt-4 text-sm leading-7 text-slate-600">
                {item.review_text || item.review || 'Beautifully planned and professionally handled from start to finish.'}
              </p>
            </article>
          ))}
        </div>
      </section>
    );
  };

  const renderJournal = (section) => {
    const content = section?.content || {};
    const items = data?.travel_journal || [];
    if (!items.length) return null;

    return (
      <section className="mx-auto max-w-7xl px-4 py-14">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9ba6ba]">Journal</p>
            <h2 className="mt-2 font-homevilas text-4xl font-semibold text-[#0d213f]">
              {content.heading || 'Travel journal'}
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              {content.subheading || 'Guides, destination notes, and inspiration from our travel team.'}
            </p>
          </div>
        </div>
        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {items.map((item) => (
            <article key={item.id} className="overflow-hidden rounded-[28px] border border-[#dde3ef] bg-white shadow-[0_16px_40px_rgba(15,23,42,0.06)]">
              <img
                src={resolveAssetUrl(item.image_url) || 'https://picsum.photos/seed/homevilas-journal/900/700'}
                alt={item.title}
                className="h-52 w-full object-cover"
              />
              <div className="p-5">
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#9ba6ba]">
                  {item.category || 'Journal'}
                </p>
                <h3 className="mt-2 text-lg font-semibold text-[#15243d]">{item.title}</h3>
                <p className="mt-2 text-sm leading-7 text-slate-500">{item.excerpt || item.read_time || 'Travel inspiration from Homevilas.'}</p>
              </div>
            </article>
          ))}
        </div>
      </section>
    );
  };

  const renderCTA = (section) => {
    const content = section?.content || {};
    return (
      <section className="bg-[#0c1d37] py-14 text-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-white/55">Design your next journey</p>
            <h2 className="mt-2 font-homevilas text-4xl font-semibold">
              {content.heading || 'Ready to design your next journey?'}
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-7 text-white/72">
              {content.subheading || 'Share your dates, budget, and travel style. We will shape a tailored proposal around you.'}
            </p>
          </div>
          <Link
            to={content.buttonLink || '/packages/list'}
            className="inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-xs font-semibold uppercase tracking-[0.24em] text-[#081b3a]"
          >
            {content.buttonText || 'Request a custom package'}
          </Link>
        </div>
      </section>
    );
  };

  if (loading) {
    return (
      <div className="space-y-8 bg-[#f7f4ee] px-4 py-10">
        <Skeleton className="mx-auto h-[420px] max-w-7xl rounded-[36px]" />
        <Skeleton className="mx-auto h-[280px] max-w-7xl rounded-[36px]" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-12">
        <div className="rounded-[32px] border border-rose-100 bg-rose-50 p-6 text-sm text-rose-700">
          {error || 'Unable to load the packages landing page.'}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#f7f4ee]">
      {renderHero(findSection('hero'))}
      {renderDestinations(findSection('destinations'))}
      {renderPackagesGrid(
        findSection('trending')?.content?.heading || 'Trending packages',
        findSection('trending')?.content?.subheading || 'Compact, high-intent itineraries for this season.',
        data.trending_packages,
        Math.max(1, Number(findSection('trending')?.content?.maxItems) || 8)
      )}
      {renderThemes(findSection('themes'))}
      {renderListingPreview(findSection('listing'))}
      {renderOffers(findSection('offers'))}
      {renderReviews(findSection('reviews'))}
      {renderJournal(findSection('journal'))}
      {renderCTA(findSection('cta'))}
      <EnquiryModal
        open={Boolean(enquiryPkg)}
        onClose={() => setEnquiryPkg(null)}
        pkg={enquiryPkg}
        source="LANDING"
        labels={{ ...(listingSettings.labels || {}), enquire: 'Request package' }}
      />
    </div>
  );
};

export default PackagesLanding;
